var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/react-hooks';
import { useMutation } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/free-solid-svg-icons';
import useAuthentication from 'hooks/use-authentication';
import useLogger from 'hooks/use-logger';
import useDateTime from 'hooks/use-date-time';
import COMPLETE_EXAM_ATTEMPT from 'mutations/complete-exam-attempt.graphql';
import GET_EXAM_BY_ID from 'queries/get-exam-by-id.graphql';
import showNotification from 'notifications/notifications';
import { getAccountIdFromStorage } from 'helpers/customBranding';
const Timer = (props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getUserIdFromToken, getExamIdFromToken } = useAuthentication();
    const logger = useLogger();
    const { currentDateTime } = useDateTime();
    const [finishAt, setFinishAt] = useState();
    const [time, setTime] = useState({
        days: -1,
        hours: -1,
        minutes: -1,
        seconds: -1,
    });
    const [internalProps, setInternalProps] = useState();
    const [getExam] = useLazyQuery(GET_EXAM_BY_ID, {
        fetchPolicy: 'network-only',
    });
    const [completeExamAttempt] = useMutation(COMPLETE_EXAM_ATTEMPT);
    const isExamDurationUnlimited = () => {
        return (props === null || props === void 0 ? void 0 : props.durationUnit) === 'UNLIMITED';
    };
    useEffect(() => {
        if (!props ||
            !props.startedAt ||
            !props.durationUnit ||
            props.durationTime === undefined) {
            return;
        }
        setInternalProps(props);
    }, [props]);
    useEffect(() => {
        if (!internalProps ||
            !internalProps.startedAt ||
            !internalProps.durationUnit ||
            !internalProps.durationTime) {
            return;
        }
        const { startedAt: startedAtString, durationUnit, durationTime, closingAt } = internalProps;
        let finishDate;
        if (isExamDurationUnlimited() && closingAt) {
            finishDate = new Date(closingAt);
        }
        else {
            finishDate = new Date(startedAtString);
            if (durationUnit === 'HOURS') {
                finishDate.setHours(finishDate.getHours() + durationTime);
            }
            if (durationUnit === 'MINUTES') {
                finishDate.setMinutes(finishDate.getMinutes() + durationTime);
            }
            if (durationUnit === 'DAYS') {
                finishDate.setDate(finishDate.getDate() + durationTime);
            }
            if (closingAt && finishDate > new Date(closingAt)) {
                finishDate = new Date(closingAt);
            }
        }
        setFinishAt(finishDate);
    }, [internalProps]);
    useEffect(() => {
        if (!currentDateTime || !finishAt) {
            return;
        }
        // get total seconds between the times
        let delta = (finishAt.valueOf() - currentDateTime.valueOf()) / 1000;
        if (delta <= 0) {
            setTime({
                days: 0,
                hours: 0,
                minutes: 0,
                seconds: 0,
            });
            return;
        }
        // calculate days
        const days = Math.floor(delta / 86400);
        // calculate and subtract hours
        const hours = Math.floor(((delta) - (days * 86400)) / 3600);
        delta -= days * 86400;
        delta -= hours * 3600;
        // calculate and subtract minutes
        const minutes = Math.floor(delta / 60) % 60;
        delta -= minutes * 60;
        // what's left is seconds
        const seconds = Math.floor(delta);
        setTime({
            days,
            hours,
            minutes,
            seconds,
        });
    }, [currentDateTime]);
    useEffect(() => {
        if (time.hours === 0 && time.minutes === 0 && time.seconds === 0) {
            isTimeExtended();
        }
    }, [time]);
    const isTimeExtended = () => {
        getExam({
            variables: {
                id: getExamIdFromToken()
            }
        }).then(response => {
            const { exam } = response.data;
            const finishDate = new Date(exam.closingAt);
            if (internalProps && internalProps.closingAt) {
                const previousClosingDate = new Date(internalProps.closingAt);
                // Validate if there is a new closingDate, if true, updates the timer and show a notification
                if (previousClosingDate < finishDate) {
                    showNotification('warning', t('FINISH.CLOSING_DATE_EXTENDED'));
                    setInternalProps(Object.assign(Object.assign({}, internalProps), { closingAt: exam.closingAt, durationTime: exam.durationTime, durationUnit: exam.durationUnit }));
                    return;
                }
            }
            else {
                // If there's no previous closingDate (for any reason), updates the timer
                setInternalProps(Object.assign(Object.assign({}, internalProps), { closingAt: exam.closingAt }));
            }
            // If the exam is UNLIMITED, closes the exam because there's no update on the closingDate
            if (exam.durationUnit === 'UNLIMITED') {
                markExamAsCompleted();
            }
            // Helper for validate wich time unit is greater
            const timesValues = {
                MINUTES: 1,
                HOURS: 2,
                DAYS: 3,
                UNLIMITED: 4
            };
            // Validate if the duration is updated
            if ((internalProps === null || internalProps === void 0 ? void 0 : internalProps.durationUnit) && (internalProps === null || internalProps === void 0 ? void 0 : internalProps.durationTime)) {
                // If the new duration has a greater unit of time, updates the timer and show a notification
                if (timesValues[internalProps.durationUnit] < timesValues[exam.durationUnit]) {
                    showNotification('warning', t('FINISH.DURATION_EXTENDED'));
                    setInternalProps(Object.assign(Object.assign({}, internalProps), { durationUnit: exam.durationUnit }));
                    return;
                }
                // If the new duration has the same unit, but the time value is greater, updates the timer and show a notification
                if (internalProps.durationTime < exam.durationTime) {
                    showNotification('warning', t('FINISH.TIME_EXTENDED'));
                    setInternalProps(Object.assign(Object.assign({}, internalProps), { durationTime: exam.durationTime }));
                    return;
                }
            }
            else {
                // If there's no previous duration values (for any reason), updates the timer
                setInternalProps(Object.assign(Object.assign({}, internalProps), { durationTime: exam.durationTime, durationUnit: exam.durationUnit }));
                return;
            }
            // If there's no updates on the closingDate or duration of the exam, closes the exam
            markExamAsCompleted();
        });
    };
    const markExamAsCompleted = () => __awaiter(void 0, void 0, void 0, function* () {
        showNotification('warning', t('FINISH.TIME_IS_OVER'));
        const fields = {
            userId: getUserIdFromToken(),
            examId: getExamIdFromToken(),
        };
        const accountId = getAccountIdFromStorage();
        try {
            yield completeExamAttempt({
                variables: {
                    fields,
                    accountId
                },
            });
        }
        catch (error) {
            logger.error(error);
            showNotification('error', t('GENERAL.ERROR'));
        }
        navigate('/finish');
    });
    const getDateNumber = (num) => {
        if (num < 0) {
            return '0';
        }
        return num > 9 ? `${num}` : `0${num}`;
    };
    return (_jsx(_Fragment, { children: time.hours !== -1 && time.minutes !== -1 && time.seconds !== -1 && (_jsxs("div", Object.assign({ className: "block--data-icon block--data-icon-success" }, { children: [_jsxs("span", Object.assign({ className: "subtitle subtitle__icon" }, { children: [t('EXAM_ONGOING.REMAINING_TIME'), " ", t('EXAM_ONGOING.TIME_COUNTER_DESCRIPTION')] }), void 0), _jsx(FontAwesomeIcon, { icon: faClock }, void 0), _jsx("h4", { children: `${getDateNumber(time.days)}:${getDateNumber(time.hours)}:${getDateNumber(time.minutes)}:${getDateNumber(time.seconds)}` }, void 0)] }), void 0)) }, void 0));
};
export default Timer;
